import { useState, useEffect } from 'react';
import { getSettings, updateSettings, testEmail, testTelegram, testNtfy } from '../services/api';
import { Settings } from '../types';

export function SettingsModal({ onClose }: { onClose: () => void }) {
  const [settings, setSettings] = useState<Settings>({
    check_interval_hours: '8',
    alert_threshold_percent: '10',
    email_enabled: 'false',
    email_to: '',
    email_from: '',
    resend_api_key: '',
    telegram_enabled: 'false',
    telegram_bot_token: '',
    telegram_chat_id: '',
    ntfy_enabled: 'false',
    ntfy_topic: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getSettings();
        setSettings(data);
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (key: keyof Settings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setTestResult(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setTestResult(null);
    try {
      await updateSettings(settings);
      setTestResult({ success: true, message: 'Paramètres sauvegardés avec succès' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: 'Erreur lors de la sauvegarde' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestResult(null);
    try {
      const result = await testEmail();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, message: 'Erreur lors du test' });
    }
  };

  const handleTestTelegram = async () => {
    setTestResult(null);
    try {
      const result = await testTelegram();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, message: 'Erreur lors du test' });
    }
  };

  const handleTestNtfy = async () => {
    setTestResult(null);
    try {
      const result = await testNtfy();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, message: 'Erreur lors du test' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          ⚙️ Paramètres généraux
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Intervalle de vérification (heures)</label>
            <select
              value={settings.check_interval_hours}
              onChange={(e) => handleChange('check_interval_hours', e.target.value)}
              className="input"
            >
              <option value="1">1 heure</option>
              <option value="6">6 heures</option>
              <option value="8">8 heures (3x/jour)</option>
              <option value="12">12 heures</option>
              <option value="24">24 heures</option>
            </select>
          </div>
          <div>
            <label className="label">Seuil d'alerte par défaut (%)</label>
            <input
              type="number"
              min="1"
              max="100"
              value={settings.alert_threshold_percent}
              onChange={(e) => handleChange('alert_threshold_percent', e.target.value)}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          📧 Notifications par email
        </h3>

        <label className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            checked={settings.email_enabled === 'true'}
            onChange={(e) => handleChange('email_enabled', e.target.checked.toString())}
            className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-white">Activer les notifications par email</span>
        </label>

        {settings.email_enabled === 'true' && (
          <div className="space-y-4" style={{ animation: 'fadeIn 0.2s' }}>
            <div>
              <label className="label">Email destinataire</label>
              <input
                type="email"
                value={settings.email_to}
                onChange={(e) => handleChange('email_to', e.target.value)}
                className="input"
                placeholder="votre@email.com"
              />
            </div>

            <div>
              <label className="label">Email expéditeur</label>
              <input
                type="text"
                value={settings.email_from}
                onChange={(e) => handleChange('email_from', e.target.value)}
                className="input"
                placeholder="My Price Tracker &lt;noreply@votredomaine.com&gt;"
              />
            </div>

            <div>
              <label className="label flex items-center justify-between">
                Clé API Resend
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  {showApiKey ? 'Masquer' : 'Afficher'}
                </button>
              </label>
              <input
                type={showApiKey ? 'text' : 'password'}
                value={settings.resend_api_key}
                onChange={(e) => handleChange('resend_api_key', e.target.value)}
                className="input"
                placeholder="re_xxxxxxxxxxxxx"
              />
              <p className="text-xs text-gray-500 mt-1">
                Obtenez votre clé API sur <a href="https://resend.com" target="_blank" rel="noopener" className="text-blue-400 hover:underline">resend.com</a>
              </p>
            </div>

            <button
              onClick={handleTestEmail}
              disabled={isSaving || !settings.resend_api_key || !settings.email_to}
              className="btn btn-info"
            >
              📧 Tester l'envoi d'email
            </button>
          </div>
        )}
      </div>

      {/* Telegram Settings */}
      <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          ✈️ Notifications Telegram
        </h3>

        <label className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            checked={settings.telegram_enabled === 'true'}
            onChange={(e) => handleChange('telegram_enabled', e.target.checked.toString())}
            className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-white">Activer les notifications Telegram</span>
        </label>

        {settings.telegram_enabled === 'true' && (
          <div className="space-y-4" style={{ animation: 'fadeIn 0.2s' }}>
            <div>
              <label className="label">Token du bot</label>
              <input
                type="password"
                value={settings.telegram_bot_token}
                onChange={(e) => handleChange('telegram_bot_token', e.target.value)}
                className="input"
                placeholder="123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"
              />
              <p className="text-xs text-gray-500 mt-1">
                Créez un bot via <a href="https://telegram.me/BotFather" target="_blank" rel="noopener" className="text-blue-400 hover:underline">@BotFather</a> sur Telegram
              </p>
            </div>

            <div>
              <label className="label">Chat ID</label>
              <input
                type="text"
                value={settings.telegram_chat_id}
                onChange={(e) => handleChange('telegram_chat_id', e.target.value)}
                className="input"
                placeholder="987654321"
              />
              <p className="text-xs text-gray-500 mt-1">
                Obtenez votre Chat ID via <a href="https://telegram.me/userinfobot" target="_blank" rel="noopener" className="text-blue-400 hover:underline">@userinfobot</a>
              </p>
            </div>

            <button
              onClick={handleTestTelegram}
              disabled={isSaving || !settings.telegram_bot_token || !settings.telegram_chat_id}
              className="btn btn-info"
            >
              ✈️ Tester Telegram
            </button>
          </div>
        )}
      </div>

      {/* ntfy Settings */}
      <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          🔔 Notifications ntfy.sh
        </h3>

        <label className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            checked={settings.ntfy_enabled === 'true'}
            onChange={(e) => handleChange('ntfy_enabled', e.target.checked.toString())}
            className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-white">Activer les notifications ntfy.sh</span>
        </label>

        {settings.ntfy_enabled === 'true' && (
          <div className="space-y-4" style={{ animation: 'fadeIn 0.2s' }}>
            <div>
              <label className="label">Nom du topic</label>
              <input
                type="text"
                value={settings.ntfy_topic}
                onChange={(e) => handleChange('ntfy_topic', e.target.value)}
                className="input"
                placeholder="laurent-pricetracker-x7f92k"
              />
              <p className="text-xs text-gray-500 mt-1">
                Utilisez un nom unique et difficile à deviner. Installez l'app <a href="https://ntfy.sh" target="_blank" rel="noopener" className="text-blue-400 hover:underline">ntfy</a> et abonnez-vous à ce topic.
              </p>
            </div>

            <button
              onClick={handleTestNtfy}
              disabled={isSaving || !settings.ntfy_topic}
              className="btn btn-info"
            >
              🔔 Tester ntfy.sh
            </button>
          </div>
        )}
      </div>

      {/* Status */}
      {testResult && (
        <div className={`p-3 rounded-lg text-sm ${
          testResult.success
            ? 'bg-green-900/30 border border-green-700 text-green-300'
            : 'bg-red-900/30 border border-red-700 text-red-300'
        }`}>
        {testResult.message}
      </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
        <button onClick={onClose} className="btn btn-secondary">Fermer</button>
        <button onClick={handleSave} disabled={isSaving} className="btn btn-primary">
          {isSaving ? 'Sauvegarde...' : '💾 Sauvegarder'}
        </button>
      </div>
    </div>
  );
}